import * as React from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import api from "../../services/api";
import { PROGRAMA_ID } from "../../config";

/**
 * Importação em massa de usuários (gestores SENAI / interlocutores) a partir de uma
 * planilha Excel - reaproveita o mesmo backend do comando ace `sgt:import-users`.
 * Fluxo em 3 passos: escolher arquivo/programa -> analisar (prévia, sem gravar nada)
 * -> confirmar (cria de fato e manda e-mail de boas-vindas).
 */
const DialogoImportarUsuarios = ({ open, onClose }) => {
  const [programas, setProgramas] = React.useState([]);
  const [programaId, setProgramaId] = React.useState("");
  const [arquivo, setArquivo] = React.useState(null);
  const [analise, setAnalise] = React.useState(null);
  const [resultado, setResultado] = React.useState(null);
  const [carregando, setCarregando] = React.useState(false);
  const [erro, setErro] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setArquivo(null);
    setAnalise(null);
    setResultado(null);
    setErro(null);
    api.http.get("/programas").then((res) => {
      setProgramas(res.data || []);
      // pelo id fixo - o programa PROCEL foi renomeado (PotencializEE Nacional)
      const procel = (res.data || []).find((p) => p.id === PROGRAMA_ID.PROCEL);
      setProgramaId(procel ? procel.id : res.data?.[0]?.id ?? "");
    });
  }, [open]);

  const analisar = async () => {
    if (!arquivo) return;
    setCarregando(true);
    setErro(null);
    try {
      const formData = new FormData();
      formData.append("file", arquivo);
      const { data } = await api.http.post("/users/importar-preview", formData);
      setAnalise(data);
    } catch (error) {
      setErro(error.response?.data?.message || error.toString());
    } finally {
      setCarregando(false);
    }
  };

  const baixarTemplate = async () => {
    setCarregando(true);
    setErro(null);
    try {
      const { data } = await api.http.get("/users/importar-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "modelo-importacao-usuarios.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setErro(error.response?.data?.message || error.toString());
    } finally {
      setCarregando(false);
    }
  };

  const confirmar = async () => {
    setCarregando(true);
    setErro(null);
    try {
      const formData = new FormData();
      formData.append("file", arquivo);
      formData.append("programaId", programaId);
      const { data } = await api.http.post("/users/importar-confirmar", formData);
      setResultado(data);
    } catch (error) {
      setErro(error.response?.data?.message || error.toString());
    } finally {
      setCarregando(false);
    }
  };

  const fechar = () => {
    onClose(!!resultado?.criados?.length);
  };

  return (
    <Dialog open={open} onClose={fechar} maxWidth="md" fullWidth>
      <DialogTitle>Importar usuários de planilha</DialogTitle>
      <DialogContent>
        {erro && <Alert severity="error" sx={{ mb: 2 }}>{erro}</Alert>}

        {!resultado && (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              A planilha precisa ter as colunas: <b>Nome completo</b>, <b>E-mail institucional</b>,{" "}
              <b>DR</b> e <b>Perfil indicado</b>. Perfis aceitos: "Gestão SENAI", "Interlocutor",{" "}
              "Consultor de mercado", "Especialista EE" ou "Consultor EE".
            </Typography>
            <Button variant="text" onClick={baixarTemplate} disabled={carregando} sx={{ mb: 2 }}>
              Baixar modelo de planilha (.xlsx)
            </Button>

            <FormControl sx={{ width: "100%", mb: 2 }}>
              <TextField
                select
                label="Programa a vincular"
                value={programaId}
                onChange={(e) => setProgramaId(e.target.value)}
              >
                {programas.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>
                ))}
              </TextField>
            </FormControl>

            <Button variant="outlined" component="label" disabled={carregando}>
              {arquivo ? arquivo.name : "Escolher planilha (.xlsx)"}
              <input
                type="file"
                accept=".xlsx"
                hidden
                onChange={(e) => {
                  setArquivo(e.target.files[0] || null);
                  setAnalise(null);
                }}
              />
            </Button>
          </>
        )}

        {carregando && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {analise && !resultado && !carregando && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {analise.totalLinhas} linha(s) lida(s) da planilha.
            </Typography>
            {analise.jaExistem.length > 0 && (
              <Alert severity="info" sx={{ mb: 1 }}>
                {analise.jaExistem.length} e-mail(s) já existem no sistema e serão ignorados.
              </Alert>
            )}
            {analise.semRole.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {analise.semRole.length} linha(s) com "Perfil indicado" não reconhecido - serão ignoradas.
              </Alert>
            )}
            {analise.semEmailOuNome.length > 0 && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {analise.semEmailOuNome.length} linha(s) sem nome ou e-mail - serão ignoradas.
              </Alert>
            )}

            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              {analise.paraCriar.length} usuário(s) serão criados:
            </Typography>
            {analise.paraCriar.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>DR</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>E-mail</TableCell>
                    <TableCell>Papel</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analise.paraCriar.map((l) => (
                    <TableRow key={l.email}>
                      <TableCell>{l.dr}</TableCell>
                      <TableCell>{l.nome}</TableCell>
                      <TableCell>{l.email}</TableCell>
                      <TableCell>{l.roleId}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {analise.jaExistem.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
                  {analise.jaExistem.length} usuário(s) já existem no sistema:
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>DR</TableCell>
                      <TableCell>Nome</TableCell>
                      <TableCell>E-mail</TableCell>
                      <TableCell>Papel indicado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analise.jaExistem.map((l) => (
                      <TableRow key={`${l.linha}-${l.email}`}>
                        <TableCell>{l.dr}</TableCell>
                        <TableCell>{l.nome}</TableCell>
                        <TableCell>{l.email}</TableCell>
                        <TableCell>{l.roleId}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </Box>
        )}

        {resultado && (
          <Alert severity="success">
            {resultado.criados.length} usuário(s) criado(s) e e-mail de boas-vindas enviado.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={fechar}>Fechar</Button>
        {!resultado && (
          <>
            {!analise ? (
              <Button variant="contained" onClick={analisar} disabled={!arquivo || carregando}>
                Analisar planilha
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={confirmar}
                disabled={carregando || analise.paraCriar.length === 0}
              >
                Confirmar importação ({analise.paraCriar.length})
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DialogoImportarUsuarios;
